/**
 * ConditionManagementPanel
 *
 * Displays lender conditions as a grouped checklist within the application
 * detail view. Conditions are grouped by type (Precedent, Subsequent, Ongoing)
 * with progress bars. Supports creating, updating status, and deleting conditions.
 *
 * F007:
 * AC001: Conditions grouped by type with progress bars
 * AC002: Add condition via modal, delete with confirmation
 * AC003: Update condition status (auto-sets satisfiedDate on 'satisfied')
 * AC004: Category badges and priority display
 * AC005: "All satisfied" state indicated
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AddConditionModal } from "@/components/modals/add-condition-modal";
import { UpdateConditionStatusModal } from "@/components/modals/update-condition-status-modal";

// ── Types ────────────────────────────────────────────────────────────────────

interface ConditionRow {
  id: string;
  applicationId: string;
  conditionType: string;
  conditionCategory: string;
  conditionDescription: string;
  conditionShortName: string | null;
  conditionStatus: string;
  conditionNumber: number | null;
  responsibleParty: string | null;
  priority: number | null;
  dueDate: string | null;
  satisfiedDate: string | null;
  satisfiedBy: string | null;
  lenderFeedback: string | null;
  brokerNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConditionManagementPanelProps {
  applicationId: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const CONDITION_TYPE_ORDER: string[] = ["precedent", "subsequent", "ongoing"];

const CONDITION_TYPE_LABELS: Record<string, string> = {
  precedent: "Precedent",
  subsequent: "Subsequent",
  ongoing: "Ongoing",
};

const CONDITION_CATEGORY_COLORS: Record<string, string> = {
  documentation: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  valuation: "bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200",
  insurance: "bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-200",
  legal: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200",
  financial: "bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200",
  security: "bg-rose-100 text-rose-800 dark:bg-rose-800 dark:text-rose-200",
  other: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

const CONDITION_CATEGORY_LABELS: Record<string, string> = {
  documentation: "Documentation",
  valuation: "Valuation",
  insurance: "Insurance",
  legal: "Legal",
  financial: "Financial",
  security: "Security",
  other: "Other",
};

const CONDITION_STATUS_COLORS: Record<string, string> = {
  outstanding: "bg-amber-100 text-amber-800",
  submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-yellow-100 text-yellow-800",
  satisfied: "bg-green-100 text-green-800",
  waived: "bg-gray-100 text-gray-800",
  not_applicable: "bg-gray-100 text-gray-500",
};

const CONDITION_STATUS_LABELS: Record<string, string> = {
  outstanding: "Outstanding",
  submitted: "Submitted",
  under_review: "Under Review",
  satisfied: "Satisfied",
  waived: "Waived",
  not_applicable: "N/A",
};

const PRIORITY_LABELS: Record<number, string> = {
  1: "High",
  2: "Medium",
  3: "Low",
};

const PRIORITY_COLORS: Record<number, string> = {
  1: "text-red-600",
  2: "text-amber-600",
  3: "text-gray-500",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function isCompletedStatus(status: string): boolean {
  return status === "satisfied" || status === "waived" || status === "not_applicable";
}

function isOverdue(condition: ConditionRow): boolean {
  if (!condition.dueDate || isCompletedStatus(condition.conditionStatus)) return false;
  return new Date(condition.dueDate) < new Date();
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── Component ────────────────────────────────────────────────────────────────

export function ConditionManagementPanel({
  applicationId,
}: ConditionManagementPanelProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [updatingCondition, setUpdatingCondition] = useState<ConditionRow | null>(null);
  const [deletingCondition, setDeletingCondition] = useState<ConditionRow | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    precedent: true,
    subsequent: true,
    ongoing: true,
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch conditions
  const { data: conditions = [], isLoading } = useQuery<ConditionRow[]>({
    queryKey: [`/api/applications/${applicationId}/conditions`],
    enabled: !!applicationId,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/conditions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/conditions`],
      });
      toast({ title: "Condition deleted" });
      setDeletingCondition(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete condition",
        variant: "destructive",
      });
    },
  });

  // Group conditions by type
  const groupedConditions: Record<string, ConditionRow[]> = {};
  for (const type of CONDITION_TYPE_ORDER) {
    groupedConditions[type] = conditions.filter((c) => c.conditionType === type);
  }

  // Calculate progress per group
  function getProgress(items: ConditionRow[]): { completed: number; total: number; percent: number } {
    const total = items.length;
    if (total === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = items.filter((c) => isCompletedStatus(c.conditionStatus)).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  }

  // Check "all precedent satisfied" state
  const precedentConditions = groupedConditions.precedent || [];
  const allPrecedentSatisfied =
    precedentConditions.length > 0 &&
    precedentConditions.every((c) => isCompletedStatus(c.conditionStatus));

  const toggleSection = (type: string) => {
    setOpenSections((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-8"
        data-testid="condition-management-panel"
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="condition-management-panel" className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {conditions.length} condition{conditions.length !== 1 ? "s" : ""}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddModal(true)}
          data-testid="button-add-condition"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add Condition
        </Button>
      </div>

      {/* All precedent satisfied banner */}
      {allPrecedentSatisfied && (
        <div
          className="flex items-center gap-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2"
          data-testid="banner-all-satisfied"
        >
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-700 dark:text-green-400">
            All precedent conditions satisfied
          </span>
        </div>
      )}

      {/* Empty state */}
      {conditions.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No conditions yet.
        </p>
      )}

      {/* Grouped sections */}
      {CONDITION_TYPE_ORDER.map((type) => {
        const items = groupedConditions[type];
        if (items.length === 0) return null;

        const progress = getProgress(items);

        return (
          <Collapsible
            key={type}
            open={openSections[type]}
            onOpenChange={() => toggleSection(type)}
          >
            <div
              className="border rounded-md"
              data-testid={`condition-section-${type}`}
            >
              {/* Section header */}
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  {openSections[type] ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">
                    {CONDITION_TYPE_LABELS[type] || type}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({progress.completed}/{progress.total})
                  </span>
                </div>
                <div
                  className="w-24"
                  data-testid={`condition-progress-${type}`}
                >
                  <Progress value={progress.percent} className="h-2" />
                </div>
              </CollapsibleTrigger>

              {/* Condition rows */}
              <CollapsibleContent>
                <div className="border-t divide-y">
                  {items.map((condition) => (
                    <div
                      key={condition.id}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${
                        isOverdue(condition) ? "bg-red-50 dark:bg-red-900/10" : ""
                      }`}
                      data-testid={`condition-row-${condition.id}`}
                    >
                      {/* Visual checkbox indicator */}
                      <div className="flex-shrink-0">
                        {isCompletedStatus(condition.conditionStatus) ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </div>

                      {/* Description + meta */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm ${
                            isCompletedStatus(condition.conditionStatus)
                              ? "line-through text-muted-foreground"
                              : ""
                          }`}
                        >
                          {condition.conditionDescription}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {/* Category badge */}
                          <Badge
                            variant="secondary"
                            className={`text-xs ${
                              CONDITION_CATEGORY_COLORS[condition.conditionCategory] || ""
                            }`}
                            data-testid={`category-badge-${condition.conditionCategory}`}
                          >
                            {CONDITION_CATEGORY_LABELS[condition.conditionCategory] ||
                              condition.conditionCategory}
                          </Badge>

                          {/* Status badge */}
                          <Badge
                            variant="secondary"
                            className={`text-xs ${
                              CONDITION_STATUS_COLORS[condition.conditionStatus] || ""
                            }`}
                            data-testid={`condition-status-badge-${condition.conditionStatus}`}
                          >
                            {CONDITION_STATUS_LABELS[condition.conditionStatus] ||
                              condition.conditionStatus}
                          </Badge>

                          {/* Priority */}
                          {condition.priority && (
                            <span
                              className={`text-xs font-medium ${
                                PRIORITY_COLORS[condition.priority] || "text-gray-500"
                              }`}
                              data-testid={`condition-priority-${condition.id}`}
                            >
                              {PRIORITY_LABELS[condition.priority] || `P${condition.priority}`}
                            </span>
                          )}

                          {/* Due date */}
                          {condition.dueDate && (
                            <span
                              className={`text-xs ${
                                isOverdue(condition)
                                  ? "text-red-600 font-medium"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {isOverdue(condition) && (
                                <AlertTriangle className="inline h-3 w-3 mr-0.5" />
                              )}
                              Due: {formatDate(condition.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setUpdatingCondition(condition)}
                          data-testid={`button-update-status-${condition.id}`}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Status
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          onClick={() => setDeletingCondition(condition)}
                          data-testid={`button-delete-condition-${condition.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}

      {/* Add Condition Modal */}
      <AddConditionModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        applicationId={applicationId}
      />

      {/* Update Status Modal */}
      {updatingCondition && (
        <UpdateConditionStatusModal
          open={!!updatingCondition}
          onOpenChange={(open) => !open && setUpdatingCondition(null)}
          condition={updatingCondition}
          applicationId={applicationId}
        />
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={!!deletingCondition}
        onOpenChange={(open) => !open && setDeletingCondition(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-condition">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Condition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this condition? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingCondition) {
                  deleteMutation.mutate(deletingCondition.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
